import React, { useContext, useState } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { CardContent } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { saveSourceFields } from 'store/sources';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';
import { AUTHORIZE_DATA_ACCESS_URL } from 'pages/Directory/consts';

import CardHeader from '@material-ui/core/CardHeader';
import SourceContext from 'helpers/SourceContext';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import Base from 'pages/Directory/Base';

interface OAuthFormParams {
  clientId?: string;
  clientSecret?: string;
  apiEndpoint?: string;
  tokenEndpoint?: string;
  authorizationEndpoint?: string;
}

function WorkdayOAuthForm() {
  const initOAuthParams: OAuthFormParams = {};

  const [oauthParams, setOAuthParams] = useState(initOAuthParams);

  const classes = useStyles();
  const analytics = useAnalytics();
  const history = useHistory();
  const dispatch = useDispatch();
  const sourceContext = useContext(SourceContext);
  const { sourceInfo } = sourceContext;

  const handleChange = (
    fieldName: string,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value.trim();

    setOAuthParams(current => ({ ...current, [fieldName]: value }));
  };

  const handleNext = async () => {
    await dispatch(saveSourceFields(sourceInfo.sourceId, oauthParams));

    const baseUrl = makeSourceCreateBaseUrl(sourceInfo.directoryId, sourceInfo.sourceId);

    history.replace(`${baseUrl}${AUTHORIZE_DATA_ACCESS_URL}`);

    analytics.track('Wizard - Workday OAuth Step Completed', {
      category: 'AD - Directories',
    });
  };

  function formIsValid(): boolean {
    return !!(
      oauthParams.clientId &&
      oauthParams.clientSecret &&
      oauthParams.apiEndpoint &&
      oauthParams.tokenEndpoint &&
      oauthParams.authorizationEndpoint
    );
  }

  return (
    <Base onNext={handleNext} disableNextButton={!formIsValid()} currentStep={1}>
      <CardHeader
        title="Register a Workday API Client for Sift"
        subheader="Create an OAuth client and enter the integration info below."
      />
      <CardContent className={classes.connectionInfoCopy}>
        <div>
          See{' '}
          <Link
            href="https://help.justsift.com/en/articles/12151333-workday-sync-setup"
            color="secondary"
          >
            this article
          </Link>{' '}
          for the required configuration to connect to Sift.
        </div>
        <div className={classes.inputContainer}>
          <TextField
            data-cy="data-client-id-input"
            label="Client ID"
            placeholder="Enter Client ID"
            onChange={e => handleChange('clientId', e)}
          />
          <TextField
            data-cy="data-client-id-input"
            label="Client Secret"
            type="password"
            placeholder="Enter Client Secret"
            onChange={e => handleChange('clientSecret', e)}
          />
          <TextField
            data-cy="data-client-id-input"
            label="API Endpoint"
            placeholder="Enter API Endpoint"
            onChange={e => handleChange('apiEndpoint', e)}
          />
          <TextField
            data-cy="data-client-id-input"
            label="Token Endpoint"
            placeholder="Enter Token Endpoint"
            onChange={e => handleChange('tokenEndpoint', e)}
          />
          <TextField
            data-cy="data-client-id-input"
            label="Authorization Endpoint"
            placeholder="Enter Authorization Endpoint"
            onChange={e => handleChange('authorizationEndpoint', e)}
          />
        </div>
      </CardContent>
    </Base>
  );
}

const useStyles = makeStyles(theme => ({
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 366,
  },
  connectionInfoCopy: {
    paddingTop: 0,
  },
}));

export default WorkdayOAuthForm;
